import type {
  DictationState,
  DictationSessionResponse,
} from "../../../types/dictation";

const STORAGE_KEY = "lingoshade.dictationDraft.v1";
const MAX_AGE_MS = 24 * 60 * 60 * 1000;

export interface DictationDraft {
  session: DictationSessionResponse;
  currentSentenceIndex: number;
  userInputs: string[];
  sentenceRepeats: Record<number, number>;
  playedSentences: Record<number, boolean>;
  hasStarted: boolean;
  elapsedTimeMs: number;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isSession(value: unknown): value is DictationSessionResponse {
  if (!isRecord(value) || !isRecord(value.settings)) return false;
  return (
    [
      "pauseBetweenWords",
      "pauseBetweenSentences",
      "playbackSpeed",
      "timeoutPerSentence",
    ].every(
      (key): boolean =>
        isRecord(value.settings) &&
        typeof value.settings[key] === "number" &&
        Number.isFinite(value.settings[key]),
    ) &&
    typeof value.settings.autoRepeat === "boolean" &&
    ["id", "textId", "content", "language", "level", "voiceId"].every(
      (key): boolean => typeof value[key] === "string",
    ) &&
    Array.isArray(value.sentences) &&
    value.sentences.length > 0 &&
    value.sentences.every(
      (sentence: unknown): boolean => typeof sentence === "string",
    )
  );
}

export function clearDictationDraft(): void {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    /* Storage can be disabled. */
  }
}

export function saveDictationDraft(
  userId: string,
  state: DictationState,
): void {
  if (!state.session || state.result) {
    clearDictationDraft();
    return;
  }
  const draft: DictationDraft = {
    session: { ...state.session, settings: state.settings },
    currentSentenceIndex: state.currentSentenceIndex,
    userInputs: state.userInputs,
    sentenceRepeats: state.sentenceRepeats,
    playedSentences: state.playedSentences,
    hasStarted: state.hasStarted,
    elapsedTimeMs: state.startTime
      ? Math.max(0, Date.now() - state.startTime)
      : 0,
  };
  try {
    sessionStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ userId, savedAt: Date.now(), draft }),
    );
  } catch {
    /* Practice remains usable when storage is unavailable. */
  }
}

export function readDictationDraft(userId: string): DictationDraft | null {
  try {
    const raw: unknown = JSON.parse(
      sessionStorage.getItem(STORAGE_KEY) || "null",
    );
    if (
      !isRecord(raw) ||
      raw.userId !== userId ||
      typeof raw.savedAt !== "number" ||
      Date.now() - raw.savedAt > MAX_AGE_MS ||
      !isRecord(raw.draft)
    )
      return null;
    const draft = raw.draft;
    if (
      !isSession(draft.session) ||
      !Array.isArray(draft.userInputs) ||
      draft.userInputs.length !== draft.session.sentences.length ||
      !draft.userInputs.every(
        (input: unknown): boolean => typeof input === "string",
      ) ||
      typeof draft.currentSentenceIndex !== "number" ||
      !Number.isInteger(draft.currentSentenceIndex) ||
      draft.currentSentenceIndex < 0 ||
      draft.currentSentenceIndex >= draft.session.sentences.length ||
      typeof draft.hasStarted !== "boolean" ||
      typeof draft.elapsedTimeMs !== "number" ||
      !Number.isFinite(draft.elapsedTimeMs) ||
      draft.elapsedTimeMs < 0 ||
      !isRecord(draft.sentenceRepeats) ||
      !Object.values(draft.sentenceRepeats).every(
        (value): boolean =>
          typeof value === "number" && Number.isInteger(value) && value >= 0,
      ) ||
      !isRecord(draft.playedSentences) ||
      !Object.values(draft.playedSentences).every(
        (value): boolean => typeof value === "boolean",
      )
    )
      return null;
    return {
      session: draft.session,
      currentSentenceIndex: draft.currentSentenceIndex,
      userInputs: draft.userInputs as string[],
      hasStarted: draft.hasStarted,
      elapsedTimeMs: draft.elapsedTimeMs,
      sentenceRepeats: draft.sentenceRepeats as Record<number, number>,
      playedSentences: draft.playedSentences as Record<number, boolean>,
    };
  } catch {
    return null;
  }
}
